import React, { useRef, useState } from 'react';
import { log } from '~/helpers';
import useTypedSelector from '~/hooks/useTypedSelector';
import { IAnnouncements } from '~/store/reducers/phone';
import api from '~/services';
import * as lib from '~/dependencies';
import RenderItem from './RenderItem';
import { Container, Body, Title, Banner } from './styles';

type AnnouncementsProps = {
  sessionId: string;
};

export enum PlayerStatusEnum {
  Playing = 'playing',
  Paused = 'paused',
  Stopped = 'stopped',
}

export default function Announcements({ sessionId }: AnnouncementsProps) {
  // REF's
  const audioEl = useRef<HTMLMediaElement>(null);
  const localAudioEl = useRef<HTMLMediaElement>(null);

  // HOOK's
  const { t } = lib.useTranslation();

  // STATE'S
  const [announId, setAnnounId] = useState<number>(0);
  const [announData, setAnnounData] = useState<string>('');
  const [audioDestinationNode, setAudioDestinationNode] = useState<
    MediaStreamAudioDestinationNode
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [playerStatus, setPlayerStatus] = useState<PlayerStatusEnum>(
    PlayerStatusEnum.Stopped
  );

  // SELECTOR'S
  const systemLocalVideo = useTypedSelector(state => state.system?.localVideo);
  const calls = useTypedSelector(state => state.callCurrent.data);
  const announcements = useTypedSelector(state => state.phone.announcements);

  const getPcBySessionId = (id: string): RTCPeerConnection | undefined => {
    if (id) {
      const session = calls.find(e => e.id?.includes(id));

      if (session) {
        const pc = session.session.sessionDescriptionHandler // @ts-ignore
          ?.peerConnection as RTCPeerConnection;

        return pc;
      }
    }

    return undefined;
  };

  const createOrGetAudioTrack = (): MediaStreamTrack | undefined => {
    if (audioEl.current && !audioDestinationNode) {
      // Create Audio Context
      const audioCtx = new AudioContext();
      // Create Audio Source Node
      const audioSourceNode = audioCtx.createMediaElementSource(
        audioEl.current
      );
      // Create the MediaStream destination
      const audioDestNode = audioCtx.createMediaStreamDestination();
      // Connect the Audio Node with the destination
      audioSourceNode.connect(audioDestNode);
      setAudioDestinationNode(audioDestNode);

      return audioDestNode.stream.getAudioTracks()[0];
    }

    return audioDestinationNode?.stream.getAudioTracks()[0];
  };

  const handleSplitAnnouncement = async () => {
    try {
      if (sessionId && systemLocalVideo) {
        const session = getPcBySessionId(sessionId);
        const track = systemLocalVideo.getTracks()[0];

        if (session && track) {
          session.getSenders().forEach(e => e.replaceTrack(track));
        }
      }
    } catch (err) {
      log.error('Failure to resume the current session. Reason: ', err);
    }
  };

  const disconnectAudioCtx = () => {
    // Disconnect current audio context
    if (audioDestinationNode) {
      audioDestinationNode.disconnect();
    }
  };

  const handleResumeAnnouncement = () => {
    try {
      const pc = getPcBySessionId(sessionId);
      const audioTrack = createOrGetAudioTrack();

      if (pc && audioEl.current && localAudioEl.current && audioTrack) {
        pc.getSenders().forEach(e => e.replaceTrack(audioTrack));
      }
    } catch (err) {
      log.error('Failure to resume the announcement. Reason: ', err);
    }
  };

  const getAnnouncementById = async (id: number) => {
    try {
      if (id !== announId) {
        const audio = await api.announcements.get.byId(id);

        return URL.createObjectURL(audio.data);
      }
    } catch (err) {
      log.error('Failure to get the announcement by id. Reason: ', err);
    }

    return announData;
  };

  const handlePlay = async (id: number) => {
    try {
      if (id === announId) {
        handleResumeAnnouncement();
        audioEl.current?.play();
        localAudioEl.current?.play();
        setPlayerStatus(PlayerStatusEnum.Playing);

        return;
      }

      if (audioEl.current && localAudioEl.current) {
        audioEl.current.pause();
        localAudioEl.current.pause();
        setAnnounId(id);
        setIsLoading(true);

        const audioSrc = await getAnnouncementById(id);
        const pc = getPcBySessionId(sessionId);
        const audioTrack = createOrGetAudioTrack();

        if (!pc) {
          throw new Error('RTCPeerConnection not found.');
        }

        setAnnounData(audioSrc);

        audioEl.current.src = audioSrc;

        if (pc && audioTrack) {
          pc.getSenders().forEach(e => e.replaceTrack(audioTrack));
        }

        audioEl.current.play();
        // Play the announcement locally.
        localAudioEl.current.src = audioEl.current.src;
        localAudioEl.current.play();
        setPlayerStatus(PlayerStatusEnum.Playing);
        setIsLoading(false);
      }
    } catch (err) {
      log.error('Failed to execute the handlePlay function.', err);
      lib.ToastMessage('error', `${t('components.announcements.error')}`);
      setIsLoading(false);
    }
  };

  const handlePause = () => {
    audioEl.current?.pause();
    localAudioEl.current?.pause();
    handleSplitAnnouncement();
    setPlayerStatus(PlayerStatusEnum.Paused);
  };

  const handleStop = () => {
    if (audioEl.current && localAudioEl.current) {
      audioEl.current.pause();
      audioEl.current.currentTime = 0;
      localAudioEl.current.pause();
      localAudioEl.current.currentTime = 0;
    }
    setPlayerStatus(PlayerStatusEnum.Stopped);
    setAnnounData('');
    handleSplitAnnouncement();
    disconnectAudioCtx();
  };

  return (
    <>
      <Banner>
        <span>{t('components.announcements.title')}</span>
      </Banner>
      <Container>
        <Title>{t('components.announcements.subTitle')}</Title>
        <Body>
          {announcements.map((e: IAnnouncements) => (
            <RenderItem
              item={e}
              key={e.id}
              handlePlay={handlePlay}
              handlePause={handlePause}
              handleStop={handleStop}
              announId={announId}
              isLoading={isLoading}
              status={playerStatus}
              audioEl={audioEl}
            />
          ))}
        </Body>
      </Container>
      <audio
        ref={audioEl}
        autoPlay
        style={{ display: 'none' }}
        onEnded={() => {
          handleSplitAnnouncement();
          disconnectAudioCtx();
          setPlayerStatus(PlayerStatusEnum.Stopped);
        }}
      />
      <audio ref={localAudioEl} autoPlay style={{ display: 'none' }} />
    </>
  );
}
